import { extractLinks } from '../../reports/lib/extract-links';

/**
 * Разбор поля ввода: темы и ссылки под ними.
 *
 * Оператор и так знает, что с чем посеял, — ему проще разложить ссылки самому,
 * чем проверять за программой. Разметка у него своя, и она двухуровневая:
 *
 *   Над Сеулом пролетел дирижабль
 *   https://www.instagram.com/kazpress/reel/DdV-PMUsOxS/
 *   https://www.instagram.com/news.qaz/reel/DdV9oAvN6T3/
 *                                    ← одна пустая строка: та же тема,
 *   https://www.instagram.com/qazaqstan.24/reel/DddwfCcg5fR/   вторая языковая
 *   https://www.instagram.com/alash_news/reel/Dddwcy-tVBi/     версия новости
 *                                    ← две пустые строки: новая тема
 *   Группа «Ұлытау» исполнила «Жұмырқылышты»
 *   https://www.instagram.com/news.qaz/reel/DddvETItuKe/
 *
 * Русская и казахская версии одной новости — это один посев: цифры под блоком
 * в отчёте суммируются по всем копиям, и разнеси их по двум темам — отчёт
 * перестанет сходиться с тем, что собирали руками.
 *
 * СОВМЕСТИМОСТЬ. Если во вводе нет ни одной двойной пустой строки, одиночная
 * снова значит границу темы — так вводили раньше, и привычная вставка не должна
 * молча схлопываться в одну тему.
 *
 * Разметки нет вовсе — значит, ссылки свалены кучей, и посевы собираются сами
 * по подписям (см. group-seeding).
 *
 * Строка без ссылки — это либо тема, либо название паблика, скопированное из
 * чата вместе со ссылкой. Различаются они длиной: тема — это фраза, название
 * паблика — одно короткое слово.
 */

/** Короче и без пробелов — это «Kazpress», а не тема новости. */
const THEME_MIN_LENGTH = 15;

export type SeedingBlock = {
  /** Тема, написанная руками. Пусто — возьмём из подписи поста. */
  theme: string;
  urls: string[];
};

export type SeedingInput = {
  blocks: SeedingBlock[];
  /**
   * Оператор разложил ссылки сам: блоки идут в отчёт как есть, в его порядке и
   * без отбора по просмотрам. false — разбираемся сами.
   */
  manual: boolean;
  /** Всего ссылок во вводе — для счётчика под полем. */
  linkCount: number;
};

function isTheme(line: string): boolean {
  return line.length >= THEME_MIN_LENGTH || line.includes(' ');
}

function isBlank(line: string): boolean {
  return /^[ \t]*$/.test(line);
}

/** Непустой кусок ввода и сколько пустых строк стояло перед ним. */
type Chunk = { lines: string[]; gapBefore: number };

function chunksOf(text: string): Chunk[] {
  const chunks: Chunk[] = [];
  let current: Chunk | null = null;
  let blanks = 0;

  // Перевод строки из Word приезжает как \r\n. Без этой замены \r остаётся в
  // «пустой» строке, она перестаёт быть пустой, и вся разметка теряется молча.
  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (isBlank(line)) {
      if (current) {
        chunks.push(current);
        current = null;
        blanks = 0;
      }
      blanks += 1;
      continue;
    }
    if (!current) {
      current = { lines: [], gapBefore: blanks };
      blanks = 0;
    }
    current.lines.push(line);
  }
  if (current) chunks.push(current);

  return chunks;
}

export function parseSeedingInput(text: string): SeedingInput {
  const chunks = chunksOf(text).map((chunk) => {
    const urls: string[] = [];
    const themes: string[] = [];
    for (const line of chunk.lines) {
      const found = extractLinks(line);
      if (found.length > 0) {
        urls.push(...found);
        continue;
      }
      const rest = line.trim();
      if (rest && isTheme(rest)) themes.push(rest);
    }
    return { gapBefore: chunk.gapBefore, urls, theme: themes[0] ?? '' };
  });

  // Пустая строка перед самым первым куском — это отступ сверху, а не граница,
  // поэтому величина разрыва берётся только между кусками.
  const strict = chunks.slice(1).some((c) => c.gapBefore >= 2);

  const blocks: SeedingBlock[] = [];
  let theme = '';
  for (const [index, chunk] of chunks.entries()) {
    /*
     * По новой разметке граница темы — две пустые строки, и этого достаточно.
     *
     * По старой пустая строка была одна на всё, и отличить «вторая языковая
     * версия той же темы» от «следующая тема» можно только по заголовку: в
     * старом вводе новую тему всегда объявляет строка над ссылками. Если
     * заголовка нет ни у куска, ни у темы, к которой он мог бы примкнуть,
     * примыкать не к чему — это отдельный блок, как было раньше.
     */
    const boundary = strict
      ? chunk.gapBefore >= 2
      : chunk.gapBefore >= 1 && (chunk.theme !== '' || theme === '');

    if (index === 0 || boundary) {
      blocks.push({ theme: chunk.theme, urls: [...chunk.urls] });
      theme = chunk.theme;
      continue;
    }
    const current = blocks[blocks.length - 1];
    current.urls.push(...chunk.urls);
    if (!current.theme && chunk.theme) {
      current.theme = chunk.theme;
      theme = chunk.theme;
    }
  }

  // Тема без ссылок в отчёт не попадёт: показывать под ней нечего.
  const kept = blocks.filter((b) => b.urls.length > 0);

  return {
    blocks: kept,
    // Одна тема с русской и казахской версиями — это тоже ручная разметка:
    // считать её по числу блоков нельзя, блок как раз один.
    manual: kept.length > 1 || kept.some((b) => b.theme !== ''),
    linkCount: kept.reduce((n, b) => n + b.urls.length, 0),
  };
}
